<script lang="ts" setup>
import {computed} from 'vue';
import {useI18n} from 'vue-i18n';
import LazyImage from '@/vue/atoms/lazy-image.vue';
import RecommendedPill from '@/vue/atoms/recommended-pill.vue';
import ReccoProgress from '@/vue/atoms/recco-progress.vue';
import {ReccoChallengeStateDTO} from '@/ts/types/dto/recco/challenge';
import {progressFractionToPct} from '@/ts/utils/pure-functions';
import {pagesPath} from '@/ts/router/pages-path';

const props = defineProps<{
  id: number;
  title: string;
  imageUrl?: string | null;
  challengeState?: ReccoChallengeStateDTO | null;
  pctCompletionProgress?: number | null;
  recommended?: boolean;
}>();

const {t: $t} = useI18n();

// The pill/progress/Resume affordances are visual-only — convey them through the
// link's name (an aria-label suppresses ALL descendant text, sr-only spans included).
const ariaLabel = computed<string>(() => {
  const prefix = props.recommended ? `${$t('recco_recommended')}, ` : '';
  const base = `${prefix}${props.title} - ${$t('explore_filter_content_type_challenge')}`;
  if (props.challengeState !== ReccoChallengeStateDTO.IN_PROGRESS) {
    return base;
  }
  const pct = progressFractionToPct(props.pctCompletionProgress ?? 0);
  return `${base}, ${$t('complete_n', {n: pct})}, ${$t('recco_resume')}`;
});
</script>

<template>
  <router-link
    :to="{name: pagesPath.content.challenge, params: {id}}"
    class="challenge-card"
    :aria-label="ariaLabel"
  >
    <recommended-pill v-if="recommended"/>

    <!-- No content-type badge: redundant inside the Challenges section (the aria-label keeps it). -->
    <div class="hexagon image-small" aria-hidden="true">
      <lazy-image :src="imageUrl ?? ''" :width="256" :height="256"/>
    </div>

    <h3 class="challenge-title text-display-4">
      {{ title }}
    </h3>

    <template v-if="challengeState === ReccoChallengeStateDTO.IN_PROGRESS">
      <!-- aria-hidden: the link name already carries the pct. -->
      <recco-progress :value="pctCompletionProgress ?? 0" class="challenge-progress" aria-hidden="true"/>
      <span class="challenge-resume" aria-hidden="true">
        {{ $t('recco_resume') }}
        <q-icon name="arrow_forward" size="18px"/>
      </span>
    </template>

    <div class="hexagon image-large" aria-hidden="true">
      <lazy-image :src="imageUrl ?? ''" :width="256" :height="256"/>
    </div>
  </router-link>
</template>

<style lang="scss" scoped>
.challenge-card {
  // Scheme-pinned like a photo (mockup: same pastel tint + dark text in both themes) —
  // re-pinning the theme vars pins the whole subtree (progress, Resume, pill, hexagons).
  // The focus ring stays theme-aware on purpose (it sits against the page).
  --Secondary-secondary-color-5: var(--Light-Scheme-Secondary-secondary-color-5, #d7c9d3);
  --Neutral-UI-neutral-ui-100: var(--Light-Scheme-Neutral-UI-neutral-ui-100, #20282e);
  --Neutral-UI-neutral-ui-10: var(--Light-Scheme-Neutral-UI-neutral-ui-10, #e8e4e0);
  --Neutral-UI-neutral-ui-0: var(--Light-Scheme-Neutral-UI-neutral-ui-0, #fff);
  --Primary-primary-color-5: var(--Light-Scheme-Primary-primary-color-5);

  @include focus-visible;
  position: relative;
  display: flex;
  flex-direction: column;
  width: 350px;
  height: $challenge-card-height;
  flex: 0 0 350px;
  border-radius: var(--border-radius);
  background: var(--Secondary-secondary-color-5);
  overflow: hidden;
  gap: var(--Spacing-spacing-4);
  // Fixed px on purpose — fixed-size poster.
  padding: 48px 42px;
  text-decoration: none;
  color: var(--Neutral-UI-neutral-ui-100);
  transition: background 0.2s ease;

  @include screen-md {
    height: $challenge-card-height-md;
  }

  @include screen-lg {
    height: $challenge-card-height-lg;
  }

  &:hover,
  &:focus-visible {
    background: color-mix(in srgb, var(--Secondary-secondary-color-5) 91%, black);

    .image-large :deep(img) {
      transform: scale(1.1);
    }
  }
}

.hexagon {
  width: 256px;
  height: 256px;
  background: var(--Primary-primary-color-5);
  clip-path: polygon(50% 0%, 93% 25%, 93% 75%, 50% 100%, 7% 75%, 7% 25%);

  :deep(img) {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
}

.image-small {
  width: 64px;
  height: 64px;
  flex-shrink: 0;
}

.image-large {
  position: absolute;
  bottom: -34px;
  right: -44px;
  width: 220px;
  height: 220px;

  :deep(img) {
    transform-origin: center;
    transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);

    @media (prefers-reduced-motion: reduce) {
      transition: none;
    }
  }
}

.challenge-title {
  margin: 0;
}

// Started state: progress bar + Resume affordance under the title, above the images.
.challenge-progress {
  width: 100%;
  z-index: 1;
}

.challenge-resume {
  z-index: 1;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: var(--Spacing-spacing-2);
  align-self: flex-start;
  padding: var(--Spacing-spacing-2) var(--Spacing-spacing-5);
  border-radius: var(--button-border-radius);
  background: var(--Neutral-UI-neutral-ui-100);
  color: var(--Neutral-UI-neutral-ui-0);
  @include font-body-sm-bold;
}
</style>
